import type { LearnerObject_LearningGoal } from '__generated__/graphql-types';

import {
  EDUCATION_LEVEL_OPTIONS,
  type EDUCATION_LEVEL_OPTIONS_TYPE,
  LEARNING_GOAL_OPTIONS,
  type LEARNING_GOAL_OPTIONS_TYPE,
} from 'bundles/onboarding-common/constants';
import type { SelectedRole, Skill } from 'bundles/onboarding-common/types';

const formatEducationLevel = (educationLevel?: EDUCATION_LEVEL_OPTIONS_TYPE) => {
  if (!educationLevel) {
    return undefined;
  }
  switch (educationLevel) {
    case EDUCATION_LEVEL_OPTIONS.EDUCATION_LEVEL_LESS_THAN_HIGH_SCHOOL_DIPLOMA:
      return 1;
    case EDUCATION_LEVEL_OPTIONS.EDUCATION_LEVEL_HIGH_SCHOOL_DIPLOMA:
      return 4;
    case EDUCATION_LEVEL_OPTIONS.EDUCATION_LEVEL_COLLEGE_NO_DEGREE:
      return 5;
    case EDUCATION_LEVEL_OPTIONS.EDUCATION_LEVEL_ASSOCIATE_DEGREE:
      return 6;
    case EDUCATION_LEVEL_OPTIONS.EDUCATION_LEVEL_BACHELOR_DEGREE:
      return 8;
    case EDUCATION_LEVEL_OPTIONS.EDUCATION_LEVEL_MASTERS_DEGREE:
      return 9;
    case EDUCATION_LEVEL_OPTIONS.EDUCATION_LEVEL_PROFESSIONAL_DEGREE:
      return 10;
    case EDUCATION_LEVEL_OPTIONS.EDUCATION_LEVEL_DOCTORATE_DEGREE:
      return 11;
    default:
      return 0;
  }
};

const formatLearningGoal = (learningGoal?: LEARNING_GOAL_OPTIONS_TYPE) => {
  if (!learningGoal) {
    return undefined;
  }
  switch (learningGoal) {
    case LEARNING_GOAL_OPTIONS.LEARNING_GOAL_IMPROVE_SKILLS:
      return 1;
    case LEARNING_GOAL_OPTIONS.LEARNING_GOAL_CHANGE_CAREER:
      return 2;
    case LEARNING_GOAL_OPTIONS.LEARNING_GOAL_LAND_NEW_JOB:
      return 3;
    case LEARNING_GOAL_OPTIONS.LEARNING_GOAL_PERSONAL_INTEREST:
      return 4;
    default:
      return 0;
  }
};

/**
 * Converts learning goal constant to GraphQL LearnerObject enum format.
 * Used for the LearnerObject_UpdateLearningGoal mutation.
 * We should use the enum format for learning goal once the LO project is launched and verified.
 */
export const formatLearningGoalForLearnerObject = (
  learningGoal: LEARNING_GOAL_OPTIONS_TYPE
): LearnerObject_LearningGoal => {
  switch (learningGoal) {
    case LEARNING_GOAL_OPTIONS.LEARNING_GOAL_IMPROVE_SKILLS:
      return 'IMPROVE_SKILLS';
    case LEARNING_GOAL_OPTIONS.LEARNING_GOAL_CHANGE_CAREER:
      return 'CHANGE_CAREER';
    case LEARNING_GOAL_OPTIONS.LEARNING_GOAL_LAND_NEW_JOB:
      return 'LAND_NEW_JOB';
    case LEARNING_GOAL_OPTIONS.LEARNING_GOAL_PERSONAL_INTEREST:
      return 'PERSONAL_INTEREST';
    default:
      return 'IMPROVE_SKILLS';
  }
};

const formatOccupation = (occupation?: Record<string, string>) => {
  if (!occupation) {
    return undefined;
  }

  if (occupation?.id) {
    return {
      typeName: 'OccupationId',
      definition: {
        id: occupation.id,
      },
    };
  } else {
    return {
      typeName: 'UserGeneratedOccupation',
      definition: {
        name: occupation.otherText ?? '',
      },
    };
  }
};

const formatSkill = (skill: Skill) => {
  return {
    typeName: 'UserInterestSkillId',
    definition: {
      skillId: skill.id,
    },
  };
};

/**
 * Converts skills array to GraphQL LearnerObject format.
 * Used for the LearnerObject_UpdateSkillsWantedV2 mutation.
 * Note: User-generated skills are not currently supported in the onboarding flow,
 * matching the behavior of the existing LearnerProfile mutation.
 */
export const formatSkillsForLearnerObject = (skills: Array<Skill>) => {
  const userInterestSkillIds = skills.map((skill) => skill.id);

  return {
    userGeneratedSkills: null,
    userInterestSkillIds: userInterestSkillIds.length > 0 ? userInterestSkillIds : null,
  };
};

const formatOccupationPreferences = (occupationPreferences?: Array<SelectedRole>) => {
  return occupationPreferences?.map((occupation) => {
    if (occupation.type === 'CUSTOM') {
      return {
        occupation: {
          typeName: 'UserGeneratedOccupation',
          definition: {
            name: occupation.name,
          },
        },
      };
    } else if (occupation.type === 'DEFAULT') {
      return {
        occupation: {
          typeName: 'OccupationId',
          definition: {
            id: occupation.id,
          },
        },
      };
    } else {
      // TODO: Handle UNSURE type
      return undefined;
    }
  });
};

export const formatDemographics = ({
  learningGoal,
  educationLevel,
  currentOccupation,
  skillsWanted,
  occupationPreferences,
  isUnsureOfSkills,
  isUnsureOfRoles,
}: {
  learningGoal?: LEARNING_GOAL_OPTIONS_TYPE;
  educationLevel?: EDUCATION_LEVEL_OPTIONS_TYPE;
  currentOccupation?: Record<string, string>;
  skillsWanted?: Array<Skill>;
  occupationPreferences?: Array<SelectedRole>;
  isUnsureOfSkills?: boolean;
  isUnsureOfRoles?: boolean;
}) => {
  return {
    educationalAttainment: formatEducationLevel(educationLevel),
    currentOccupation: formatOccupation(currentOccupation),
    learningGoal: formatLearningGoal(learningGoal),
    skillsWantedV2: !isUnsureOfSkills ? skillsWanted?.map(formatSkill) : [],
    interestedCareers: formatOccupationPreferences(occupationPreferences),
    lastIndicatedUnsureSkillsAt: isUnsureOfSkills ? Date.now() : undefined,
    lastIndicatedUnsureRolesAt: isUnsureOfRoles ? Date.now() : undefined,
  };
};

// Handles the 'UNSURE' case for the roleSelectAdvancer question.
// TODO: Remove this and replace with an implementation using LO once that project is released.
export const mapAdvancerRoleValueForUserProfile = (
  value: SelectedRole[] | null | undefined
): {
  occupationPreferences: SelectedRole[];
  currentOccupation?: Record<string, string>;
  isUnsureOfRoles: boolean;
} => {
  const isUnsure = !value || value.some((role) => role.type === 'UNSURE');

  if (isUnsure) {
    return {
      occupationPreferences: [],
      currentOccupation: undefined,
      isUnsureOfRoles: true,
    };
  }

  const firstRole = value[0];
  let currentOccupation: Record<string, string> | undefined;

  if (!firstRole) {
    currentOccupation = { id: '' };
  } else if (firstRole.type === 'CUSTOM') {
    currentOccupation = { otherText: firstRole.name };
  } else {
    currentOccupation = { id: firstRole.id };
  }

  return {
    occupationPreferences: value,
    currentOccupation,
    isUnsureOfRoles: false,
  };
};

export const formatInterestedCareersForLearnerObject = (
  roles: SelectedRole[] | null | undefined
): {
  occupationIds: string[] | null;
  userGeneratedOccupations: string[] | null;
} => {
  if (!roles || roles.length === 0) {
    return {
      occupationIds: null,
      userGeneratedOccupations: null,
    };
  }

  const occupationIds: string[] = [];
  const userGeneratedOccupations: string[] = [];

  roles.forEach((role) => {
    if (role.type === 'DEFAULT') {
      occupationIds.push(role.id);
    } else if (role.type === 'CUSTOM') {
      userGeneratedOccupations.push(role.name);
    }
  });

  return {
    occupationIds: occupationIds.length > 0 ? occupationIds : null,
    userGeneratedOccupations: userGeneratedOccupations.length > 0 ? userGeneratedOccupations : null,
  };
};
